'use client'

import { createContext, useContext, type ReactNode } from 'react'

import { type DisplayDateAnchor } from '@/i18n/dates'

import { useWidgetState, type WidgetState, type WidgetStateTuple } from './use-widget-state'

/** `null` is "no surface-owned selection here" — a widget then owns its own. */
const SearchSelectionContext = createContext<WidgetStateTuple | null>(null)

interface SearchSelectionProviderProps {
    /** The surface's initial values, supplied once for every presentation beneath. */
    initial?: Partial<WidgetState>
    /** Required, as for any owner of a selection — see `useWidgetState`. */
    dateAnchor: DisplayDateAnchor
    children: ReactNode
}

/**
 * Holds **one** caller-owned selection for every widget presentation rendered beneath
 * it: a page mounting more than one presentation puts them in
 * lockstep without either becoming the other's parent, and one owner means one reader
 * and one writer of the stored selection.
 *
 * Feature-scoped: mounted by the pages that need it, not by a layout
 * (`components.md#effects-vs-providers`).
 */
export function SearchSelectionProvider({
    initial,
    dateAnchor,
    children,
}: SearchSelectionProviderProps) {
    const selection = useWidgetState(initial, dateAnchor)

    return (
        <SearchSelectionContext.Provider value={selection}>
            {children}
        </SearchSelectionContext.Provider>
    )
}

/** The surface-owned selection, or `null` when no provider encloses the caller. */
export function useSearchSelection(): WidgetStateTuple | null {
    return useContext(SearchSelectionContext)
}
